import { useContext } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';

import {
	Button,
	FocusRingVariants,
	FontSize,
	FontSizes,
	FontWeights,
	PaletteColor,
} from '@calm-web/design-system';
import { ChevronDown } from '@calm-web/icons';

import { OfferBannerContext } from '@/components/app/layout/OfferBannerProvider';
import Image from '@/components/image/NextImage';
import { useAnalytics } from '@/hooks/analytics';
import { useUserEligibility } from '@/hooks/user/useUserEligibility';
import { useRouterPush } from '@/hooks/utils/useRouterPush';
import { calmLogger } from '@/utils/calmLogger';

import messages from './messages';
import {
	Body,
	ButtonContainer,
	Copy,
	DownArrow,
	Heading,
	ImageContainer,
	MarqueValueProp,
	Wrapper,
} from './styles';

export type SubpageHeroProps = {
	heroHeading: MessageDescriptor;
	heroBody: MessageDescriptor;
	heroCta: MessageDescriptor;
	heroBgImage: string;
	showDownArrow?: boolean;
	CTAButtonProperties?: { href?: string; label: string; onClick?: () => void };
	secondaryCTAButtonProperties?: { href?: string; label: string; onClick?: () => void };
	isCenteredCopy?: boolean;
	heroHeaderColor?: PaletteColor;
	heroBodyColor?: PaletteColor;
	heroBodySize?: FontSize;
	primaryCTAColor?: PaletteColor;
	marqueValueProp?: MessageDescriptor;
};

const Hero = ({
	heroHeading,
	heroCta,
	heroBody,
	heroBgImage,
	heroHeaderColor,
	heroBodyColor,
	heroBodySize,
	showDownArrow = true,
	CTAButtonProperties,
	secondaryCTAButtonProperties,
	isCenteredCopy = false,
	primaryCTAColor,
	marqueValueProp,
}: SubpageHeroProps) => {
	const { isLoggedInEligibleToSubscribe, isLoggedInValid } = useUserEligibility();
	const { logEvent } = useAnalytics();
	const { formatMessage } = useIntl();
	const routerPush = useRouterPush();
	const { isBannerVisible } = useContext(OfferBannerContext);

	const getButtonProperties = (
		isSecondary?: boolean,
	): [
		label: string,
		href?: { pathname: string; queryParams?: Record<string, string | null | undefined> },
		onClick?: () => void,
	] => {
		if (CTAButtonProperties && !isSecondary) {
			return [
				CTAButtonProperties.label,
				CTAButtonProperties.href ? { pathname: CTAButtonProperties.href } : undefined,
				CTAButtonProperties.onClick,
			];
		}

		if (secondaryCTAButtonProperties && isSecondary) {
			return [
				secondaryCTAButtonProperties.label,
				secondaryCTAButtonProperties.href ? { pathname: secondaryCTAButtonProperties.href } : undefined,
				secondaryCTAButtonProperties.onClick,
			];
		}

		if (isLoggedInValid) {
			return [formatMessage(messages.ctaActiveSub), { pathname: '/app' }];
		}

		if (isLoggedInEligibleToSubscribe) {
			return [formatMessage(messages.ctaLoggedInIneligible), { pathname: '/subscribe' }];
		}

		return [formatMessage(heroCta), { pathname: '/signup-flow', queryParams: { source: 'homepage' } }];
	};

	const [label, href, primaryOnClick] = getButtonProperties();
	const [secondaryLabel, secondaryHref, secondaryOnClick] = getButtonProperties(true);

	const onClick = async (href?: {
		pathname: string;
		queryParams?: Record<string, string | null | undefined>;
	}) => {
		logEvent({
			eventName: 'Subpage : CTA : Clicked',
			eventProps: {
				section: 'hero',
			},
		});

		if (href) {
			await routerPush(href.pathname, href.queryParams);
		}
	};

	const handleClick = (
		href?: { pathname: string; queryParams?: Record<string, string | null | undefined> },
		_onClick?: () => void,
	) => {
		if (_onClick) {
			_onClick();
		}
		onClick(href).catch(err => calmLogger.error('Error in Hero onClick', {}, err));
	};

	const onDownArrowClick = () => {
		window.scrollTo({ top: window.innerHeight, behavior: 'smooth' });
	};

	return (
		<Wrapper isBannerVisible={isBannerVisible}>
			<Copy centered={isCenteredCopy}>
				<Heading el="h1" color={heroHeaderColor ?? 'bannerText'} align="left" noMargin>
					{formatMessage(heroHeading, {
						br: <br />,
					})}
				</Heading>
				<Body size={heroBodySize ?? FontSizes.base} el="p" color={heroBodyColor ?? 'blackAlpha80'}>
					{formatMessage(heroBody) + ' '}
					{marqueValueProp && (
						<MarqueValueProp weight={FontWeights.Demi} color={'blue7'}>
							{formatMessage(marqueValueProp)}
						</MarqueValueProp>
					)}
				</Body>
				<ButtonContainer>
					<Button
						fullWidth
						backgroundColor={primaryCTAColor ?? 'buttonPrimaryBg'}
						textColor="white"
						onPress={() => handleClick(href, primaryOnClick)}
						focusRingVariant={FocusRingVariants.Dark}
					>
						{label}
					</Button>
					{secondaryCTAButtonProperties && (
						<Button
							fullWidth
							backgroundColor="buttonSecondaryBg"
							textColor="buttonSecondaryText"
							onPress={() => handleClick(secondaryHref, secondaryOnClick)}
							focusRingVariant={FocusRingVariants.Light}
						>
							{secondaryLabel}
						</Button>
					)}
				</ButtonContainer>
				{showDownArrow && (
					<DownArrow
						onPress={onDownArrowClick}
						color="gray3"
						aria-label={formatMessage(messages.buttonAriaLabel)}
						Icon={ChevronDown}
						focusRingVariant={FocusRingVariants.Dark}
					/>
				)}
			</Copy>
			<ImageContainer>
				<Image priority loading="eager" fill src={heroBgImage} alt="" style={{ objectFit: 'cover' }} />
			</ImageContainer>
		</Wrapper>
	);
};

export default Hero;
